/**
 * Privacidad y Términos de uso.
 *
 * Dos rutas públicas (`/privacidad`, `/terminos`), sin sesión, con el mismo
 * armazón: encabezado con la marca y un texto largo y legible. No es la
 * landing de mercadeo ni el admin — es un documento, así que la tipografía
 * manda y no hay nada más en la pantalla que compita con ella.
 *
 * El contenido describe lo que el sistema **realmente hace** (se armó
 * revisando el código: qué se guarda, con quién se comparte, qué cookies
 * hay), no una plantilla genérica. No reemplaza una revisión legal — está
 * marcado al pie para que el dueño lo confirme antes de darlo por definitivo.
 */

import { Link } from 'react-router-dom';
import { Brandmark } from '@/components/common/Brandmark';

function LegalHeader() {
  return (
    <div className="legal-nav">
      <Link to="/" className="legal-nav__logo">
        <Brandmark size={30} />
        <span className="legal-nav__wordmark">CleanBlue</span>
      </Link>
      <Link to="/" className="legal-nav__back">← Volver al sitio</Link>
    </div>
  );
}

function LegalPage({ title, updated, children }: {
  title: string; updated: string; children: React.ReactNode;
}) {
  return (
    <div className="legal">
      <LegalHeader />
      <article className="legal__wrap">
        <header className="legal__head">
          <h1>{title}</h1>
          <p className="legal__updated">Última actualización: {updated}</p>
        </header>
        {children}
      </article>
    </div>
  );
}

export function PrivacyPage() {
  return (
    <LegalPage title="Política de privacidad" updated="18 de agosto de 2026">
      <p>
        Esta política explica qué datos guarda Clean Blue de quienes usan este sitio y la
        aplicación —consultorios clientes, sus médicos, y el personal de limpieza— y qué se hace
        con ellos. Está escrita en base a lo que el sistema hace hoy, no como plantilla genérica.
      </p>

      <h2>Quién trata los datos</h2>
      <p>
        Clean Blue, empresa de limpieza profesional con sede en Costa Rica. Para cualquier
        consulta sobre esta política o sus datos personales, escriba a{' '}
        <a href="mailto:contacto@cleanblue.com">contacto@cleanblue.com</a> o al{' '}
        <a href="tel:86376556">8637-6556</a>.
      </p>

      <h2>Qué datos guardamos</h2>
      <ul>
        <li>
          <b>Cuenta de acceso:</b> nombre, correo, teléfono y, si la persona lo sube, una foto de
          perfil. La contraseña nunca se guarda en texto plano.
        </li>
        <li>
          <b>Datos de facturación</b> (de quien paga el servicio): razón social, cédula física o
          jurídica, correo y teléfono de facturación, y dirección — los datos mínimos para emitir
          una factura electrónica válida en Costa Rica.
        </li>
        <li>
          <b>Datos del consultorio:</b> nombre, código interno, dirección, y el historial de
          visitas, cobros y solicitudes de servicio asociado a él.
        </li>
        <li>
          <b>Datos del personal:</b> horario de visitas asignadas, horas extra reportadas, y
          evaluaciones de calidad del servicio realizado.
        </li>
        <li>
          <b>Comprobantes de pago:</b> si la persona sube un comprobante de SINPE Móvil o
          transferencia, ese archivo se guarda de forma privada, solo accesible por quien lo subió
          y por el personal administrativo.
        </li>
      </ul>

      <h2>Lo que no hacemos</h2>
      <p>
        No usamos cookies de rastreo, píxeles publicitarios ni herramientas de analítica de
        terceros (nada de Google Analytics ni similares). La única cookie que este sitio usa es
        la de sesión, técnica y necesaria para mantener la sesión iniciada — no sirve para
        rastrear a nadie fuera de esta aplicación, y se borra al cerrar sesión.
      </p>
      <p>
        Tampoco procesamos ni guardamos números de tarjeta: los pagos se hacen por SINPE Móvil o
        transferencia bancaria, directo a la cuenta de Clean Blue: la plataforma solo refleja el
        estado del cobro.
      </p>

      <h2>Con quién se comparten los datos</h2>
      <p>
        Con el proveedor de facturación electrónica que Clean Blue usa para cumplir con Hacienda:
        recibe los datos mínimos de cada comprobante (nombre o razón social, cédula, monto). No
        vendemos ni alquilamos datos a nadie, y no los compartimos con fines publicitarios.
      </p>
      <p>
        Los botones de contacto del sitio ("Solicitar visita", "Elegir paquete") abren WhatsApp
        con un mensaje precargado — ese mensaje lo recibe el WhatsApp de Clean Blue, como
        cualquier conversación normal de WhatsApp.
      </p>

      <h2>Cuánto tiempo se guardan</h2>
      <p>
        Mientras dure la relación con el consultorio o la persona empleada, y después el tiempo
        que exige la legislación fiscal y laboral costarricense sobre comprobantes y registros de
        planilla. Una cuenta que ya no se usa puede pedir su eliminación por los medios de
        contacto de arriba, sujeto a esas obligaciones legales de conservar comprobantes.
      </p>

      <h2>Sus derechos</h2>
      <p>
        Conforme a la Ley 8968 de Protección de la Persona frente al Tratamiento de sus Datos
        Personales, puede pedir acceso, rectificación, cancelación u oposición sobre sus datos
        escribiendo a <a href="mailto:contacto@cleanblue.com">contacto@cleanblue.com</a>.
      </p>

      <h2>Seguridad</h2>
      <p>
        Las contraseñas se guardan solo como huella (hash), nunca en texto plano. La sesión viaja
        en una cookie que el navegador no expone a scripts. El acceso dentro de la aplicación está
        separado por rol: un médico solo ve su propio consultorio, el personal solo ve su propio
        horario, y solo el equipo administrativo ve el panorama completo.
      </p>

      <p className="legal__note">
        Este texto lo redactó Claude junto con el dueño de Clean Blue, describiendo el
        funcionamiento real del sistema. No reemplaza una revisión por un abogado, y conviene
        confirmarlo con uno antes de tratarlo como definitivo.
      </p>
    </LegalPage>
  );
}

export function TermsPage() {
  return (
    <LegalPage title="Términos de uso" updated="18 de agosto de 2026">
      <p>
        Estos términos aplican al usar la aplicación de Clean Blue: el portal de clientes, la
        vista de personal, y el panel administrativo. Al iniciar sesión, se aceptan.
      </p>

      <h2>El servicio</h2>
      <p>
        Clean Blue ofrece un sistema para coordinar visitas de limpieza, ver facturas y su
        estado de pago, y gestionar el personal asignado a cada consultorio. No es una tienda ni
        una pasarela de pago: los pagos se hacen por SINPE Móvil o transferencia bancaria directo
        a Clean Blue, y la aplicación solo refleja ese estado.
      </p>

      <h2>Cuentas</h2>
      <p>
        Las cuentas las crea Clean Blue o quien administra un consultorio — no hay registro
        público abierto. Cada persona es responsable de mantener su contraseña en privado y de
        avisar si sospecha que alguien más accedió a su cuenta.
      </p>

      <h2>Precios y paquetes</h2>
      <p>
        Los precios y el IVA que se muestran pueden cambiar. Un cambio de precio nunca afecta un
        cobro ya emitido: cada factura conserva el precio vigente el día que se generó.
      </p>

      <h2>Uso aceptable</h2>
      <p>
        La aplicación es para coordinar el servicio de limpieza contratado. No está permitido
        usarla para fines distintos, intentar acceder a datos de otro consultorio o persona
        empleada, ni compartir credenciales de acceso con terceros.
      </p>

      <h2>Disponibilidad</h2>
      <p>
        Hacemos lo razonable para mantener el sistema disponible, pero puede haber
        interrupciones por mantenimiento o fuera de nuestro control. Para solicitudes urgentes de
        limpieza, el teléfono y WhatsApp (<a href="tel:86376556">8637-6556</a>) siguen siendo el
        canal más directo.
      </p>

      <h2>Propiedad</h2>
      <p>
        El diseño, el código y el contenido de este sitio y la aplicación son de Clean Blue. Los
        datos que cada consultorio o persona empleada carga (facturas, fotos, horarios) siguen
        siendo suyos — Clean Blue los guarda para prestar el servicio, no los reclama como propios.
      </p>

      <h2>Terminación</h2>
      <p>
        Cualquiera de las partes puede terminar la relación de servicio. Al hacerlo, se conservan
        los datos por el tiempo que exige la legislación fiscal costarricense (ver la{' '}
        <Link to="/privacidad">política de privacidad</Link>), y el resto se puede eliminar a
        pedido.
      </p>

      <h2>Ley aplicable</h2>
      <p>Estos términos se rigen por las leyes de Costa Rica.</p>

      <p className="legal__note">
        Este texto lo redactó Claude junto con el dueño de Clean Blue, describiendo el
        funcionamiento real del sistema. No reemplaza una revisión por un abogado, y conviene
        confirmarlo con uno antes de tratarlo como definitivo.
      </p>
    </LegalPage>
  );
}
